import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/helpers";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList } from "recharts";
import { FileSpreadsheet, Printer, Loader2, Trash2, HandCoins } from "lucide-react";
import CollectOldDuesDialog from "./CollectOldDuesDialog";

const COLORS = ["#4F46E5", "#10B981", "#F59E0B", "#E11D48", "#0EA5E9", "#8B5CF6", "#14B8A6"];

function StaffCollection({ rows }) {
  const [period, setPeriod] = useState("day");
  const key = (d) => (period === "day" ? d : (d || "").slice(0, 7));

  const groups = {};       // staff -> period -> {count, cash, other, total}
  const staffTotals = {};  // staff -> {count, cash, other, total}
  (rows || []).forEach((r) => {
    const staff = r.collected_by || "Unknown";
    const pk = key(r.date);
    const isCash = (r.mode || "").toLowerCase() === "cash";
    groups[staff] = groups[staff] || {};
    const g = groups[staff][pk] || { count: 0, cash: 0, other: 0, total: 0 };
    g.count += 1; g.total += r.amount; if (isCash) g.cash += r.amount; else g.other += r.amount;
    groups[staff][pk] = g;
    const t = staffTotals[staff] || { count: 0, cash: 0, other: 0, total: 0 };
    t.count += 1; t.total += r.amount; if (isCash) t.cash += r.amount; else t.other += r.amount;
    staffTotals[staff] = t;
  });

  const staffNames = Object.keys(groups).sort();
  const grand = Object.values(staffTotals).reduce((a, t) => ({
    count: a.count + t.count, cash: a.cash + t.cash, other: a.other + t.other, total: a.total + t.total,
  }), { count: 0, cash: 0, other: 0, total: 0 });

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
          <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-xs uppercase text-slate-400 font-semibold">Total Collected</div><div className="text-xl font-heading font-extrabold text-emerald-600 tabular">{fmt(grand.total)}</div></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-xs uppercase text-slate-400 font-semibold">Cash</div><div className="text-xl font-heading font-extrabold text-slate-800 tabular">{fmt(grand.cash)}</div></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-xs uppercase text-slate-400 font-semibold">Other Modes</div><div className="text-xl font-heading font-extrabold text-slate-800 tabular">{fmt(grand.other)}</div></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><div className="text-xs uppercase text-slate-400 font-semibold">Collectors</div><div className="text-xl font-heading font-extrabold tabular">{staffNames.length}</div></div>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden no-print" data-testid="period-toggle">
          <button onClick={() => setPeriod("day")} className={`px-4 py-2 text-sm font-medium ${period === "day" ? "bg-indigo-600 text-white" : "bg-white text-slate-600"}`} data-testid="period-day">Daily</button>
          <button onClick={() => setPeriod("month")} className={`px-4 py-2 text-sm font-medium ${period === "month" ? "bg-indigo-600 text-white" : "bg-white text-slate-600"}`} data-testid="period-month">Monthly</button>
        </div>
      </div>

      {staffNames.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 py-16 text-center text-slate-400">No collections in this period.</div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="staff-collection-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3">Staff / {period === "day" ? "Date" : "Month"}</th>
                <th className="text-right px-4 py-3">Transactions</th>
                <th className="text-right px-4 py-3">Cash</th>
                <th className="text-right px-4 py-3">Other Modes</th>
                <th className="text-right px-4 py-3">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {staffNames.map((staff) => {
                const periods = Object.keys(groups[staff]).sort().reverse();
                const st = staffTotals[staff];
                return (
                  <React.Fragment key={staff}>
                    <tr className="bg-indigo-50/60 font-semibold text-slate-800" data-testid={`staff-total-${staff}`}>
                      <td className="px-4 py-2.5">{staff}</td>
                      <td className="px-4 py-2.5 text-right tabular">{st.count}</td>
                      <td className="px-4 py-2.5 text-right tabular">{fmt(st.cash)}</td>
                      <td className="px-4 py-2.5 text-right tabular">{fmt(st.other)}</td>
                      <td className="px-4 py-2.5 text-right tabular text-emerald-700">{fmt(st.total)}</td>
                    </tr>
                    {periods.map((pk) => {
                      const g = groups[staff][pk];
                      return (
                        <tr key={staff + pk} className="text-slate-600">
                          <td className="px-4 py-2 pl-8 text-slate-500">{pk}</td>
                          <td className="px-4 py-2 text-right tabular">{g.count}</td>
                          <td className="px-4 py-2 text-right tabular">{fmt(g.cash)}</td>
                          <td className="px-4 py-2 text-right tabular">{fmt(g.other)}</td>
                          <td className="px-4 py-2 text-right tabular font-medium">{fmt(g.total)}</td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OldDuesReport() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [collectSid, setCollectSid] = useState(null);
  const load = () => api.get("/reports/old-dues").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!data) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-indigo-500" /></div>;
  const rows = data.students.filter((s) => { const t = q.trim().toLowerCase(); return !t || (s.name || "").toLowerCase().includes(t) || (s.admission_number || "").toLowerCase().includes(t); });
  return (
    <div className="space-y-4" data-testid="old-dues-report">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-xl border border-slate-200 px-4 py-2"><div className="text-xs text-slate-400">Students</div><div className="font-heading font-extrabold tabular" data-testid="old-dues-count">{data.count}</div></div>
          <div className="bg-rose-50 rounded-xl border border-rose-200 px-4 py-2"><div className="text-xs text-rose-600">Total Old Dues</div><div className="font-heading font-extrabold tabular text-rose-600" data-testid="old-dues-total">{fmt(data.total)}</div></div>
        </div>
        <div className="flex gap-2 no-print">
          <Input placeholder="Search name / adm no…" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" data-testid="old-dues-search" />
          <Button variant="outline" onClick={() => window.print()} data-testid="old-dues-print"><Printer size={16} className="mr-2" />Print</Button>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-auto">
        {rows.length === 0 ? <div className="py-16 text-center text-slate-400">No students carrying old dues.</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr>
              <th className="px-3 py-2.5 text-left">Student</th><th className="px-3 py-2.5 text-left">Class</th><th className="px-3 py-2.5 text-left">Session</th><th className="px-3 py-2.5 text-left">Carried From</th><th className="px-3 py-2.5 text-left">Label</th><th className="px-3 py-2.5 text-right">Old Dues</th><th className="px-3 py-2.5 text-right no-print">Action</th>
            </tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id} className="border-t border-slate-100" data-testid={`old-dues-row-${s.admission_number}`}>
                  <td className="px-3 py-2.5"><div className="font-medium text-slate-800">{s.name}</div><div className="text-xs text-slate-400">{s.admission_number}</div></td>
                  <td className="px-3 py-2.5 text-slate-600">{`${s.class_name} ${s.section}`.trim() || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{s.academic_session || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{s.from_session || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{s.label}</td>
                  <td className="px-3 py-2.5 text-right tabular font-semibold text-rose-600">{fmt(s.amount)}</td>
                  <td className="px-3 py-2.5 text-right no-print">
                    {s.locked ? (
                      <span className="text-xs text-slate-400" title="Locked to the current session (block-old). Collect from the student's fee page." data-testid={`old-dues-locked-${s.admission_number}`}>Current session only</span>
                    ) : (
                      <Button size="sm" variant="outline" className="h-8 text-amber-700 border-amber-300 hover:bg-amber-50" onClick={() => setCollectSid(s.student_id)} data-testid={`old-dues-collect-${s.admission_number}`}><HandCoins size={14} className="mr-1" />Collect</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <CollectOldDuesDialog open={!!collectSid} onOpenChange={(v) => { if (!v) setCollectSid(null); }} studentId={collectSid} onCollected={load} />
    </div>
  );
}

export default function Reports() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [col, setCol] = useState(null);
  const [exp, setExp] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nameFilter, setNameFilter] = useState("");
  const [modeFilter, setModeFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [selected, setSelected] = useState([]);
  const [showSug, setShowSug] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const token = localStorage.getItem("token");

  const delPayment = async (id, receipt) => {
    if (!window.confirm(`Delete receipt ${receipt}? This cannot be undone.`)) return;
    try { await api.delete(`/payments/${id}`); toast.success("Receipt deleted"); setSelected([]); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };
  const bulkDelPayments = async (ids) => {
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} selected receipt(s)? This cannot be undone.`)) return;
    try { await Promise.all(ids.map((id) => api.delete(`/payments/${id}`))); toast.success("Receipts deleted"); setSelected([]); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const load = () => {
    setLoading(true);
    const params = {};
    if (from) params.date_from = from;
    if (to) params.date_to = to;
    Promise.all([api.get("/reports/collection", { params }), api.get("/reports/expenses", { params })])
      .then(([c, e]) => { setCol(c.data); setExp(e.data); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const toPie = (obj) => Object.entries(obj || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-heading text-3xl font-extrabold text-slate-900">Reports</h1>
        <div className="flex gap-2 items-end no-print">
          <div><Label className="text-xs">From</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" /></div>
          <Button onClick={load} data-testid="apply-filter-btn">Apply</Button>
          <Button variant="outline" onClick={() => window.print()}><Printer size={16} className="mr-2" />Print</Button>
        </div>
      </div>

      {loading || !col ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-indigo-500" /></div> : (
        <Tabs defaultValue="fees">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <TabsList><TabsTrigger value="fees" data-testid="tab-fee-report">Fee Collection</TabsTrigger><TabsTrigger value="staff" data-testid="tab-staff-report">Staff Collection</TabsTrigger><TabsTrigger value="exp" data-testid="tab-exp-report">Expenses</TabsTrigger><TabsTrigger value="olddues" data-testid="tab-olddues-report">Old Dues</TabsTrigger></TabsList>
            {(() => {
              const income = col.total || 0;
              const expense = exp?.total || 0;
              const status = income - expense;
              const surplus = status >= 0;
              return (
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-heading font-bold" data-testid="report-pnl-summary">
                  <span className="text-slate-500 font-semibold text-xs">Income</span>
                  <span className="text-emerald-600 tabular" data-testid="pnl-income">{fmt(income)}</span>
                  <span className="text-slate-400">−</span>
                  <span className="text-slate-500 font-semibold text-xs">Expense</span>
                  <span className="text-rose-600 tabular" data-testid="pnl-expense">{fmt(expense)}</span>
                  <span className="text-slate-400">=</span>
                  <span className={`text-xs font-semibold ${surplus ? "text-emerald-600" : "text-rose-600"}`}>{surplus ? "Surplus" : "Deficit"}</span>
                  <span className={`tabular ${surplus ? "text-emerald-600" : "text-rose-600"}`} data-testid="pnl-status">{fmt(Math.abs(status))}</span>
                </div>
              );
            })()}
          </div>

          <TabsContent value="fees" className="mt-4 space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5"><div className="text-xs uppercase text-slate-400 font-semibold">Total Collected</div><div className="text-2xl font-heading font-extrabold text-emerald-600 tabular">{fmt(col.total)}</div></div>
              <div className="bg-white rounded-xl border border-slate-200 p-5"><div className="text-xs uppercase text-slate-400 font-semibold">Transactions</div><div className="text-2xl font-heading font-extrabold tabular">{col.count}</div></div>
              <a href={`${API}/reports/collection/excel?token=${token}${from ? `&date_from=${from}` : ""}${to ? `&date_to=${to}` : ""}`} className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-3 hover:border-emerald-300 no-print" data-testid="export-excel-btn"><FileSpreadsheet size={28} className="text-emerald-600" /><div><div className="font-semibold text-slate-800">Export Excel</div><div className="text-xs text-slate-400">Download .xlsx</div></div></a>
            </div>
            <div className="grid lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6"><h3 className="font-heading font-bold mb-3">By Payment Mode</h3><ResponsiveContainer width="100%" height={240}><PieChart><Pie data={toPie(col.by_mode)} dataKey="value" nameKey="name" outerRadius={80} label>{toPie(col.by_mode).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={(v) => fmt(v)} /><Legend /></PieChart></ResponsiveContainer></div>
              <div className="bg-white rounded-xl border border-slate-200 p-6"><h3 className="font-heading font-bold mb-3">By Class</h3>{(() => {
                const byClassData = Object.entries(col.by_class || {}).map(([name, value]) => ({ name, value, pct: col.total ? Math.round((value / col.total) * 1000) / 10 : 0 })).sort((a, b) => b.value - a.value);
                if (byClassData.length === 0) return <div className="text-sm text-slate-400 py-10 text-center">No collection data.</div>;
                return (
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={byClassData} margin={{ left: 4, right: 8, top: 24, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#475569" }} interval={0} angle={byClassData.length > 6 ? -30 : 0} textAnchor={byClassData.length > 6 ? "end" : "middle"} height={byClassData.length > 6 ? 60 : 30} />
                      <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} tickFormatter={(v) => fmt(v)} />
                      <Tooltip formatter={(v, n, p) => [`${fmt(v)} (${p.payload.pct}% of total)`, "Collected"]} />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {byClassData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        <LabelList dataKey="value" position="top" content={({ x, y, width, value, index }) => (
                          <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fill="#334155">{`${fmt(value)} · ${byClassData[index].pct}%`}</text>
                        )} />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                );
              })()}</div>
            </div>
            {(() => {
              const q = nameFilter.trim().toLowerCase();
              const feeRows = col.rows.filter((r) =>
                (modeFilter === "all" || r.mode === modeFilter) &&
                (!dateFilter || r.date === dateFilter) &&
                (!q || (r.student_name || "").toLowerCase().includes(q) || (r.father_name || "").toLowerCase().includes(q)));
              const shown = feeRows.slice(0, 200);
              const selRows = selected.length ? feeRows.filter((r) => selected.includes(r.receipt_number)) : feeRows;
              const totalAmount = selRows.reduce((a, r) => a + (r.amount || 0), 0);
              const allSel = shown.length > 0 && shown.every((r) => selected.includes(r.receipt_number));
              const toggle = (rn) => setSelected((s) => s.includes(rn) ? s.filter((x) => x !== rn) : [...s, rn]);
              const toggleAll = () => setSelected(allSel ? [] : shown.map((r) => r.receipt_number));
              return (
                <>
                  <div className="flex flex-wrap items-center gap-2 no-print">
                    <div className="relative max-w-xs w-full">
                      <Input placeholder="Search student or father name…" value={nameFilter} onChange={(e) => { setNameFilter(e.target.value); setShowSug(true); }} onFocus={() => setShowSug(true)} onBlur={() => setTimeout(() => setShowSug(false), 150)} data-testid="report-name-filter" />
                      {showSug && nameFilter.trim() && (() => {
                        const sq = nameFilter.trim().toLowerCase();
                        const seen = new Set();
                        const sug = [];
                        for (const r of col.rows) {
                          const kk = `${r.student_name}|${r.father_name}`;
                          if (seen.has(kk)) continue;
                          if ((r.student_name || "").toLowerCase().includes(sq) || (r.father_name || "").toLowerCase().includes(sq)) {
                            seen.add(kk); sug.push(r);
                          }
                          if (sug.length >= 8) break;
                        }
                        if (sug.length === 0) return null;
                        return (
                          <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-testid="report-name-suggestions">
                            {sug.map((r, i) => (
                              <button key={i} onMouseDown={() => { setNameFilter(r.student_name); setShowSug(false); }} className="w-full text-left px-3 py-2 hover:bg-indigo-50" data-testid={`report-suggestion-${i}`}>
                                <div className="font-medium text-slate-800 text-sm">{r.student_name}</div>
                                <div className="text-xs text-slate-400">Father: {r.father_name || "—"}</div>
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    <Select value={modeFilter} onValueChange={setModeFilter}>
                      <SelectTrigger className="w-44" data-testid="report-mode-filter"><SelectValue placeholder="All Modes" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Modes</SelectItem>
                        {Object.keys(col.by_mode || {}).map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} className="w-44" data-testid="report-date-filter" />
                    {(nameFilter || modeFilter !== "all" || dateFilter || selected.length > 0) && <Button variant="ghost" className="text-slate-500" onClick={() => { setNameFilter(""); setModeFilter("all"); setDateFilter(""); setSelected([]); }} data-testid="report-clear-filter">Clear</Button>}
                    {isAdmin && selected.length > 0 && <Button variant="outline" className="text-rose-600 border-rose-200" onClick={() => bulkDelPayments(feeRows.filter((r) => selected.includes(r.receipt_number)).map((r) => r.id))} data-testid="report-bulk-delete"><Trash2 size={15} className="mr-1" />Delete ({selected.length})</Button>}
                    <div className="ml-auto text-right bg-white rounded-lg border border-slate-200 px-4 py-2" data-testid="report-total-amount">
                      <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Total Amount{selected.length ? ` (${selected.length} selected)` : ""}</div>
                      <div className="text-lg font-heading font-extrabold text-emerald-600 tabular">{fmt(totalAmount)}</div>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    <div className="px-4 py-2 text-xs text-slate-400 no-print">{feeRows.length} of {col.rows.length} transactions</div>
                    <table className="w-full text-sm"><thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="px-3 py-3 w-8"><input type="checkbox" className="accent-indigo-600 w-4 h-4" checked={allSel} onChange={toggleAll} data-testid="report-select-all" /></th><th className="text-left px-4 py-3">Se.</th><th className="text-left px-4 py-3">Student</th><th className="text-left px-4 py-3">Class</th><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Receipt</th><th className="text-right px-4 py-3">Amount</th><th className="text-left px-4 py-3">Mode</th>{isAdmin && <th className="text-right px-4 py-3 no-print">Action</th>}</tr></thead>
                      <tbody className="divide-y divide-slate-100">{shown.map((r, i) => <tr key={r.receipt_number || i} data-testid={`fee-report-row-${i}`} className={selected.includes(r.receipt_number) ? "bg-indigo-50/40" : ""}><td className="px-3 py-2"><input type="checkbox" className="accent-indigo-600 w-4 h-4" checked={selected.includes(r.receipt_number)} onChange={() => toggle(r.receipt_number)} data-testid={`report-select-${i}`} /></td><td className="px-4 py-2 text-slate-500 tabular">{i + 1}</td><td className="px-4 py-2"><div className="font-medium text-slate-800">{r.student_name}</div><div className="text-xs text-slate-400">{r.father_name || "—"}</div></td><td className="px-4 py-2">{r.class_name}</td><td className="px-4 py-2">{fmtDate(r.date)}</td><td className="px-4 py-2 font-mono text-xs">{r.receipt_number}</td><td className="px-4 py-2 text-right tabular font-medium">{fmt(r.amount)}</td><td className="px-4 py-2">{r.mode}</td>{isAdmin && <td className="px-4 py-2 text-right no-print"><Button size="sm" variant="ghost" className="text-rose-600 h-8 px-2" onClick={() => delPayment(r.id, r.receipt_number)} data-testid={`report-delete-${i}`}><Trash2 size={15} /></Button></td>}</tr>)}
                      {feeRows.length === 0 && <tr><td colSpan={isAdmin ? 9 : 8} className="text-center py-8 text-slate-400">No transactions match the filters.</td></tr>}</tbody>
                    </table>
                  </div>
                </>
              );
            })()}
          </TabsContent>

          <TabsContent value="staff" className="mt-4">
            <StaffCollection rows={col.rows} />
          </TabsContent>

          <TabsContent value="exp" className="mt-4 space-y-6">
            <div className="grid lg:grid-cols-2 gap-6 items-start">
              <div className="bg-white rounded-xl border border-slate-200 p-5 self-start"><div className="text-xs uppercase text-slate-400 font-semibold">Total Expenses</div><div className="text-2xl font-heading font-extrabold text-rose-600 tabular">{fmt(exp.total)}</div></div>
              <div className="bg-white rounded-xl border border-slate-200 p-6"><h3 className="font-heading font-bold mb-3">By Category</h3><ResponsiveContainer width="100%" height={260}><PieChart><Pie data={toPie(exp.by_category)} dataKey="value" nameKey="name" outerRadius={90} label>{toPie(exp.by_category).map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip formatter={(v) => fmt(v)} /><Legend /></PieChart></ResponsiveContainer></div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 table-scroll" data-testid="report-expenses-table">
              {(exp.rows || []).length === 0 ? <div className="py-16 text-center text-slate-400">No expenses in this period.</div> : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Category</th><th className="text-left px-4 py-3">Paid For</th><th className="text-left px-4 py-3">Paid To</th><th className="text-left px-4 py-3">Approved By</th><th className="text-left px-4 py-3">Paid By</th><th className="text-right px-4 py-3">Amount</th><th className="text-left px-4 py-3">Mode</th><th className="text-left px-4 py-3">Description</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {(exp.rows || []).map((e, i) => (
                      <tr key={e.id || i} className="hover:bg-slate-50" data-testid={`report-expense-row-${i}`}>
                        <td className="px-4 py-3">{fmtDate(e.date)}</td>
                        <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full bg-slate-100 text-xs">{e.category}</span>{e.source === "payroll" && <span className="ml-1.5 px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold">Payroll</span>}</td>
                        <td className="px-4 py-3 text-slate-500">{e.paid_for}</td>
                        <td className="px-4 py-3 text-slate-500">{e.paid_to}</td>
                        <td className="px-4 py-3 text-slate-500">{e.approved_by}</td>
                        <td className="px-4 py-3 text-slate-500">{e.paid_by}</td>
                        <td className="px-4 py-3 text-right font-semibold tabular text-rose-600">{fmt(e.amount)}</td>
                        <td className="px-4 py-3">{e.payment_mode}</td>
                        <td className="px-4 py-3 text-slate-600">{e.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>

          <TabsContent value="olddues" className="mt-4">
            <OldDuesReport />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
